/* How it works app reels: the scenes for How it works' five feature screens
   (snippets/st-app-reel-hiw.liquid). st-app-reels.js plays them like Home's, finding them here
   as each reel starts; this file only describes them. Each reel shows both ends of Sentai: the
   At home card says what is said to and by the hub, and the app shows what the family sees.
   As delivered, and with reduced motion, every screen shows the end of its reel.
   Timings and states: docs/APP_SLIDER.md. */
window.SentaiAppReelScenes = Object.assign(window.SentaiAppReelScenes || {}, (() => {
  'use strict';
  const SPRING = 'cubic-bezier(.34,1.45,.64,1)';

  // Typing into an outlined text field: focus, the label floats, characters arrive.
  const fill = async (d, field, value, speed = 70) => {
    await d.tap(field, { x: .3, y: field.offsetHeight > 80 ? .25 : .5 });
    field.classList.add('is-focused');
    await d.wait(200);
    await d.type(field.querySelector('.st-ui-field__value'), value, { speed });
    field.classList.remove('is-focused');
    field.classList.add('is-filled');
  };

  // Scroll a screen's content, here also in the still and stopped views: with the phone this
  // large, the panel shows the top 470pt or so of the screen, and a reel scrolls to what it needs.
  const scroll = (d, element, top, duration = 900) => {
    if (!d.instant) return d.scrollTo(element, top, { duration });
    element.classList.add('is-scrolled');
    element.scrollTop = top;
    return Promise.resolve();
  };

  // Something the reel adds to a screen (marked data-st-add) joins its layout and pops in.
  const add = (d, element, options = {}) => {
    element.classList.add('is-added');
    return d.pop(element, { duration: 600, ...options });
  };

  // What is said at home. It waits hidden above the phone until its moment, then each line is
  // spoken in turn: the stack opens for it, the bubble grows from its speaker and the words
  // arrive at a talking pace. Its foot stays put, so it grows upwards as the conversation goes on.
  const home = {
    find(d) {
      const card = d.screen.closest('.st-phone')?.querySelector('.st-home');
      if (card && !d.instant) card.querySelectorAll('.st-home__line').forEach(line => {
        line.dataset.stHeight = line.offsetHeight;
        line.style.display = 'none';
      });
      return card;
    },
    async show(d, card) {
      if (!card) return;
      card.classList.remove('is-pending');
      await d.play(card, [{ opacity: 0, transform: `translateY(${-14 * d.pt}px) scale(.96)` }, { opacity: 1, transform: 'none' }], { duration: 600, easing: SPRING });
    },
    async say(d, card, index, { after = 350 } = {}) {
      const line = card?.querySelectorAll('.st-home__line')[index];
      if (!line) return;
      line.style.display = '';
      if (d.instant) return;
      const gap = parseFloat(getComputedStyle(card).rowGap) || 0;
      const avatar = line.querySelector('.st-home__avatar');
      const bubble = line.querySelector('.st-home__bubble');
      const words = line.querySelector('.st-home__words');
      // The avatar and bubble stay hidden until their own entrances, which hold their first
      // frame through any delay, so neither shows and then vanishes as it starts.
      [avatar, bubble, words].forEach(part => { part.style.opacity = 0; });
      line.style.overflow = 'hidden';
      await d.play(line, [{ height: '0px', marginTop: `${-gap}px` }, { height: `${line.dataset.stHeight}px`, marginTop: '0px' }], { duration: 320, easing: 'cubic-bezier(.2,0,0,1)', fill: 'none' });
      line.style.overflow = '';
      bubble.style.transformOrigin = line.classList.contains('st-home__line--reply') ? '100% 100%' : '0 100%';
      d.play(avatar, [{ opacity: 0, transform: 'scale(.5)' }, { opacity: 1, transform: 'none' }], { duration: 350, easing: SPRING });
      await d.play(bubble, [{ opacity: 0, transform: 'scale(.6)' }, { opacity: 1, transform: 'none' }], { duration: 450, delay: 120, easing: SPRING });
      words.style.opacity = '';
      await d.stream(words, { stagger: 150 });
      await d.wait(after);
    }
  };

  // Activity, Insights, Today: the daily wellbeing check happens at home, and its result
  // reaches Mary's phone as a notification while today's score fills in. The AI summary is the
  // day's, written once a day, so it stays as it is.
  const insight = {
    length: 9200,
    async play(d) {
      const card = home.find(d);
      const banner = d.$('.st-ui-banner');
      const wellbeing = d.$('.st-ui-stat--wellbeing');
      await d.wait(400);
      await home.show(d, card);
      await home.say(d, card, 0);
      await home.say(d, card, 1, { after: 400 });
      await d.play(banner, [{ opacity: 0, transform: `translateY(${-70 * d.pt}px) scale(.94)` }, { opacity: 1, transform: 'none' }], { duration: 700, easing: SPRING });
      await d.wait(500);
      await d.pop(wellbeing.querySelector('.st-ui-num'), { duration: 600 });
      await d.pop(wellbeing.querySelector('.st-ui-stat__trend'), { duration: 450 });
      d.play(wellbeing, [{ backgroundColor: 'rgba(202,236,186,0)' }, { backgroundColor: 'rgba(202,236,186,.55)', offset: .3 }, { backgroundColor: 'rgba(202,236,186,0)' }], { duration: 1400, fill: 'none' });
    }
  };

  // Profile, then the call: Mary calls the hub from Becky's profile, Sentai asks Becky at home
  // and puts the call through.
  const call = {
    length: 10900,
    async play(d) {
      const profile = d.$('.st-ui-page--profile');
      const page = d.$('.st-ui-page--call');
      const status = page.querySelector('.st-ui-call__status');
      const dots = page.querySelector('.st-ui-call__dots');
      const avatar = page.querySelector('.st-ui-call__avatar');
      const card = home.find(d);
      const now = status.textContent;
      if (!d.instant) status.textContent = status.dataset.stWas;
      await d.wait(700);
      await d.tap(d.$('[data-st-call]'));
      d.lift();
      dots.classList.add('is-on');
      await d.navigate(profile, page);
      await d.wait(1300);
      // At home, the hub asks Becky first.
      await home.show(d, card);
      await home.say(d, card, 0);
      await home.say(d, card, 1, { after: 200 });
      // Put through.
      dots.classList.remove('is-on');
      if (d.instant) return;
      await d.play(status, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `translateY(${-4 * d.pt}px)` }], { duration: 220, fill: 'none' });
      status.textContent = now;
      await d.reveal(status, { duration: 420, y: 6 * d.pt });
      for (let i = 0; i < 3; i++) {
        const ring = document.createElement('span');
        ring.className = 'st-ui-call__ring';
        avatar.append(ring);
        d.play(ring, [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.35)' }], { duration: 1600, easing: 'ease-out', delay: i * 500 })
          .then(() => ring.remove(), () => ring.remove());
      }
      await d.wait(1800);
    }
  };

  // Mary's lock screen: Becky asks for help at home, the phone wakes with Sentai's priority
  // notification, and Sentai tells Becky.
  const requests = {
    length: 8100,
    async play(d) {
      const card = home.find(d);
      const wallpaper = d.$('.st-ui-lock__wallpaper');
      const notification = d.$('.st-ui-lock__notification');
      await d.wait(500);
      await home.show(d, card);
      await home.say(d, card, 0, { after: 300 });
      // The phone wakes as the notification arrives.
      d.play(wallpaper, [{ filter: 'brightness(.6)' }, { filter: 'brightness(1)' }], { duration: 500 });
      await d.play(notification, [
        { opacity: 0, transform: `translateY(${24 * d.pt}px) scale(.92)` },
        { opacity: 1, transform: 'none' }
      ], { duration: 650, easing: SPRING });
      await d.wait(700);
      await home.say(d, card, 1, { after: 0 });
    }
  };

  // Profile, then Add reminder: a daily physio reminder is set in the app, and at ten o'clock
  // Sentai reminds Becky at home.
  const reminders = {
    length: 14000,
    async play(d) {
      const profile = d.$('.st-ui-page--profile');
      const page = d.$('.st-ui-page--add');
      const chip = d.$('.st-ui-pchip--new');
      const card = home.find(d);
      await d.wait(500);
      // + on Reminders opens Add reminder.
      await d.tap(d.$('.st-ui-panel--reminders .st-ui-pchip--plus'));
      d.lift();
      await d.navigate(profile, page);
      await fill(d, page.querySelector('[data-st-field="name"]'), 'Physio exercises', 55);
      await fill(d, page.querySelector('[data-st-field="text"]'), 'Time for your physio exercises, Becky.', 26);
      await d.tap(page.querySelector('.st-ui-save-button'));
      d.lift();
      // Back on Profile, the list moves up to Reminders and the new one joins it.
      await d.navigate(page, profile, { back: true });
      const main = profile.querySelector('.st-ui-profile-main');
      await scroll(d, main, d.offset(chip.closest('.st-ui-section'), main) - 62 * d.pt, 800);
      await add(d, chip);
      await d.wait(400);
      // At home, at ten.
      await home.show(d, card);
      await home.say(d, card, 0);
      await home.say(d, card, 1, { after: 0 });
    }
  };

  // Swap one label for another: the old one fades away as the new one rises in.
  const swapText = async (d, element, text) => {
    await d.play(element, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: `translateY(${-4 * d.pt}px)` }], { duration: 220, fill: 'none' });
    element.textContent = text;
    await d.reveal(element, { duration: 420, y: 6 * d.pt });
  };

  // Family & friends access, then Edit member: John is given Shopping, and his line in the list
  // says so. At home, what Becky adds to her list now reaches him too.
  const access = {
    length: 13300,
    async play(d) {
      const list = d.$('.st-ui-page--list');
      const edit = d.$('.st-ui-page--edit');
      const john = d.$('.st-ui-member--john');
      const line = john.querySelector('.st-ui-member__access');
      const card = home.find(d);
      const now = line.textContent;
      if (!d.instant) line.textContent = line.dataset.stWas;
      await d.wait(300);
      await d.reveal(d.$$('.st-ui-member'), { stagger: 160, y: 16 });
      await d.wait(500);
      await d.tap(john, { x: .45 });
      d.lift();
      await d.navigate(list, edit);
      // Down to App access.
      const main = edit.querySelector('.st-ui-settings-main');
      await d.scrollTo(main, d.offset(edit.querySelector('[data-st-toggle="shopping"]').closest('.st-ui-toggle-row'), main) - 22 * d.pt, { duration: 1000 });
      const shopping = edit.querySelector('[data-st-toggle="shopping"]');
      await d.tap(shopping);
      shopping.classList.add('is-on');
      await d.wait(450);
      await d.tap(edit.querySelector('.st-ui-save-button'));
      d.lift();
      await d.navigate(edit, list, { back: true });
      await swapText(d, line, now);
      d.play(john, [{ boxShadow: `0 0 0 0 rgba(0,39,126,.35)` }, { boxShadow: `0 0 0 ${8 * d.pt}px rgba(0,39,126,0)` }], { duration: 900, easing: 'ease-out', fill: 'none' });
      await d.wait(300);
      // At home.
      await home.show(d, card);
      await home.say(d, card, 0);
      await home.say(d, card, 1, { after: 0 });
    }
  };

  return { 'hiw-insight': insight, 'hiw-call': call, 'hiw-reminders': reminders, 'hiw-access': access, 'hiw-requests': requests };
})());
